// =============================================================================
// Ad & measurement tags, gated by the visitor's consent choice
// -----------------------------------------------------------------------------
// Nothing here loads until canRunAdTags() says yes: Meta and TikTok scripts are
// never fetched in a consent region before the visitor accepts. Both platforms'
// own consent switches (fbq consent grant/revoke, ttq grant/revokeConsent) are
// driven from the stored choice, so a refusal pauses every fire — including the
// platforms' own automatic logging.
// =============================================================================

import {
  META_PIXEL_ID,
  META_PIXEL_ID_2,
  TIKTOK_PIXEL_ID,
  canRunAdTags,
  ensureConsentResolved,
  subscribeToConsentChanges,
} from "./consent";

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
    ttq?: ((...args: unknown[]) => void) & { methods?: string[] };
  }
}

let metaLoaded = false;
let metaInitialised = false;
let tiktokLoaded = false;
let routesWatched = false;

// ---------------------------------------------------------------------------
// Meta Pixel (base code)
// ---------------------------------------------------------------------------

function loadMetaScript(): void {
  if (metaLoaded) return;
  metaLoaded = true;

  // Standard Meta base code (fbevents.js), unminified.
  (function (f: Window & typeof globalThis, b: Document, e: string, v: string) {
    if (f.fbq) return;
    const n: any = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    f.fbq = n;
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    const t = b.createElement(e) as HTMLScriptElement;
    t.async = true;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    s.parentNode?.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
}

function startMetaPixel(): void {
  loadMetaScript();

  if (!metaInitialised) {
    metaInitialised = true;
    // Meta's own consent switch, set before init so nothing can fire unasked.
    window.fbq?.("consent", "grant");
    // Page views are sent below, not by the tag itself — declared before init.
    window.fbq?.("set", "autoEventLogging", false);
    window.fbq?.("init", META_PIXEL_ID);
    window.fbq?.("init", META_PIXEL_ID_2);
  } else {
    // Accepted again after a withdrawal; the instance is still in place.
    window.fbq?.("consent", "grant");
  }

  sendMetaPageView();
}

/** Refusal pauses every fire — including the tag's own automatic page logging. */
function stopMetaPixel(): void {
  if (!metaInitialised) return;
  try {
    window.fbq?.("consent", "revoke");
  } catch {
    /* an unavailable call must not break the page */
  }
}

function sendMetaPageView(): void {
  if (!metaInitialised || !canRunAdTags()) return;
  try {
    window.fbq?.("track", "PageView");
  } catch {
    /* never let a tracking hiccup break the page */
  }
}

// ---------------------------------------------------------------------------
// TikTok Pixel — installed with the same consent gate
// ---------------------------------------------------------------------------

function loadTikTokScript(): void {
  if (tiktokLoaded) return;
  tiktokLoaded = true;

  const w = window as any;
  w.TiktokAnalyticsObject = "ttq";
  const ttq = (w.ttq = w.ttq || []);

  ttq.methods = [
    "page", "track", "identify", "instances", "debug", "on", "off", "once",
    "ready", "alias", "group", "enableCookie", "disableCookie", "holdConsent",
    "revokeConsent", "grantConsent",
  ];
  ttq.setAndDefer = function (target: any, method: string) {
    target[method] = function () {
      target.push([method].concat(Array.prototype.slice.call(arguments, 0)));
    };
  };
  for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
  ttq.instance = function (id: string) {
    const instance = ttq._i[id] || [];
    for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(instance, ttq.methods[i]);
    return instance;
  };
  ttq.load = function (id: string, options?: Record<string, unknown>) {
    const url = "https://analytics.tiktok.com/i18n/pixel/events.js";
    ttq._i = ttq._i || {};
    ttq._i[id] = [];
    ttq._i[id]._u = url;
    ttq._t = ttq._t || {};
    ttq._t[id] = +new Date();
    ttq._o = ttq._o || {};
    ttq._o[id] = options || {};
    const partner = options && (options as { partner?: string }).partner;
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = url + "?sdkid=" + id + "&lib=" + w.TiktokAnalyticsObject;
    if (partner) script.src += "&partner=" + partner;
    const first = document.getElementsByTagName("script")[0];
    first.parentNode?.insertBefore(script, first);
  };

  ttq.load(TIKTOK_PIXEL_ID);
}

function startTikTokPixel(): void {
  loadTikTokScript();
  try {
    (window as any).ttq?.grantConsent?.();
  } catch {
    /* an unavailable call must not break the page */
  }
  sendTikTokPageView();
}

function stopTikTokPixel(): void {
  try {
    (window as any).ttq?.revokeConsent?.();
  } catch {
    /* an unavailable call must not break the page */
  }
}

function sendTikTokPageView(): void {
  if (!tiktokLoaded || !canRunAdTags()) return;
  try {
    (window as any).ttq?.page?.();
  } catch {
    /* never let a tracking hiccup break the page */
  }
}

// ---------------------------------------------------------------------------
// Page views across the SPA
// ---------------------------------------------------------------------------

function sendPageViews(): void {
  sendMetaPageView();
  sendTikTokPageView();
}

function watchRoutes(): void {
  if (routesWatched) return;
  routesWatched = true;

  const notify = () => sendPageViews();

  try {
    const push = window.history.pushState.bind(window.history);
    const replace = window.history.replaceState.bind(window.history);
    window.history.pushState = function (...args: Parameters<History["pushState"]>) {
      push(...args);
      // Fire after the router has had a chance to render the new route.
      setTimeout(notify, 0);
    };
    window.history.replaceState = function (...args: Parameters<History["replaceState"]>) {
      replace(...args);
      setTimeout(notify, 0);
    };
  } catch {
    /* history not patchable — initial page view still counts */
  }

  window.addEventListener("popstate", notify);
  window.addEventListener("hashchange", notify);
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

let started = false;

function applyConsent(): void {
  if (canRunAdTags()) {
    startMetaPixel();
    startTikTokPixel();
  } else {
    stopMetaPixel();
    stopTikTokPixel();
  }
}

export async function startAdTracking(): Promise<void> {
  if (started || typeof window === "undefined") return;
  started = true;

  await ensureConsentResolved();
  watchRoutes();
  applyConsent();

  // A later acceptance resumes the tags; a withdrawal pauses them again —
  // including a choice made in another open tab.
  subscribeToConsentChanges(applyConsent);
}

// ---------------------------------------------------------------------------
// Purchase-funnel events (AddToCart, InitiateCheckout, Purchase)
// ---------------------------------------------------------------------------

type FunnelEvent = "AddToCart" | "InitiateCheckout" | "Purchase" | "Lead";
const TIKTOK_EVENT: Record<FunnelEvent, string> = {
  AddToCart: "AddToCart",
  InitiateCheckout: "InitiateCheckout",
  Purchase: "CompletePayment",
  Lead: "SubmitForm",
};

/** Sends a funnel event to Meta and TikTok — only when consent allows it. */
export function trackAdEvent(
  event: FunnelEvent,
  params: { value?: number; content_ids?: string[]; num_items?: number } = {},
  dedupeKey?: string,
): void {
  try {
    if (!canRunAdTags()) return;
    if (dedupeKey) {
      const k = `adev_${event}_${dedupeKey}`;
      if (localStorage.getItem(k)) return;
      localStorage.setItem(k, "1");
    }
    const data: Record<string, unknown> = { currency: "NGN", content_type: "product", ...params };
    if (metaInitialised) window.fbq?.("track", event, data);
    if (tiktokLoaded) {
      (window as any).ttq?.track?.(TIKTOK_EVENT[event], {
        currency: "NGN",
        value: params.value,
        content_type: "product",
        contents: (params.content_ids || []).map((id) => ({ content_id: id })),
      });
    }
  } catch {
    /* never let tracking break the page */
  }
}
